import * as React from 'react';
import { Link } from '@tanstack/react-router';
import {
  Sparkles,
  Camera,
  FileText,
  ExternalLink,
  ArrowRight,
  Check,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

/**
 * Welcome tour (#135) — first-login modal for clinic owners.
 *
 * 3-step product walkthrough; dismissable; never re-shown after dismissal.
 * Storage uses localStorage with a per-tenant key so different demo logins
 * don't share the dismissal state. (Full server-side `welcome_seen_at`
 * column was deferred — localStorage is enough for a per-browser fix.)
 *
 * Mounted at /_app layout so it appears once across any route after login.
 */

const STORAGE_KEY_PREFIX = 'h2m:welcome-seen:';

export interface WelcomeTourProps {
  /** Stable tenant id (or 'operator' when role=operator). */
  tenantKey: string;
  /** Suppress for the operator role — they don't need an owner tour. */
  enabled: boolean;
  /** Friendly clinic name for the salutation. */
  clinicName?: string | null;
  /** Tenant slug used to render the public subdomain in the tour copy. */
  tenantSlug?: string | null;
}

interface Step {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  body: React.ReactNode;
}

export function WelcomeTour({ tenantKey, enabled, clinicName, tenantSlug }: WelcomeTourProps) {
  const host = tenantSlug
    ? `${tenantSlug}.pozyskajpacjenta.pl`
    : 'twoja-klinika.pozyskajpacjenta.pl';
  const storageKey = STORAGE_KEY_PREFIX + tenantKey;
  const [open, setOpen] = React.useState(false);
  const [step, setStep] = React.useState(0);

  React.useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;
    const seen = window.localStorage.getItem(storageKey);
    if (!seen) setOpen(true);
  }, [enabled, storageKey]);

  const dismiss = React.useCallback(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(storageKey, new Date().toISOString());
    }
    setOpen(false);
  }, [storageKey]);

  const steps: Step[] = [
    {
      title: clinicName ? `Witaj, ${clinicName}` : 'Witaj w pozyskajpacjenta.pl',
      icon: Sparkles,
      body: (
        <div className="space-y-3">
          <p>
            Wasza strona kliniki już istnieje pod{' '}
            <span className="font-mono">{host}</span> i czeka na trzy proste rzeczy
            zanim będzie gotowa do pokazania pacjentom.
          </p>
          <p className="text-sm text-text-muted">
            Ten samouczek zajmie 90 sekund. Możecie też pominąć i wrócić tu kiedykolwiek
            — wszystkie kroki są zapisane na Pulpicie.
          </p>
        </div>
      ),
    },
    {
      title: 'Krok 1: wgraj 6 zdjęć',
      icon: Camera,
      body: (
        <div className="space-y-3">
          <p>
            Wymagamy <strong>6 prawdziwych zdjęć</strong> przed publikacją:
          </p>
          <ul className="space-y-1 text-sm text-text-muted">
            <li className="flex items-start gap-2">
              <Check className="mt-0.5 size-3.5 text-accent" aria-hidden="true" />
              <span>4 zdjęcia wnętrz (recepcja, gabinet, sala zabiegowa, fasada)</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="mt-0.5 size-3.5 text-accent" aria-hidden="true" />
              <span>1 portret zespołu</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="mt-0.5 size-3.5 text-accent" aria-hidden="true" />
              <span>1 zbliżenie sprzętu (USG, CT, fotel dentystyczny)</span>
            </li>
          </ul>
          <p className="text-xs text-text-subtle">
            Bez prawdziwych zdjęć strona pokaże zastępcze ikony — pacjenci to wyczuwają.
          </p>
        </div>
      ),
    },
    {
      title: 'Krok 2: sprawdź treść i opublikuj',
      icon: FileText,
      body: (
        <div className="space-y-3">
          <p>
            Treść została wygenerowana z publicznych danych o klinice. Zweryfikuj ją w{' '}
            <strong>Treść strony</strong> i wprowadź poprawki, gdzie potrzeba.
          </p>
          <p>
            Gdy wszystko gra — przejdź do <strong>Publikacja</strong>, zobacz porównanie
            podgląd ↔ live i opublikuj jednym kliknięciem.
          </p>
          <p className="text-xs text-text-subtle">
            Po publikacji strona zaczyna serwować pacjentom pod
            <span className="font-mono"> {host}</span>. Własną domenę (np.{' '}
            <span className="font-mono">klinika.pl</span>) podłączymy w Ustawienia.
          </p>
        </div>
      ),
    },
  ];

  const current = steps[step]!;
  const isLast = step === steps.length - 1;
  const Icon = current.icon;

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : dismiss())}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="mb-3 inline-flex size-10 items-center justify-center rounded-full bg-accent/15 text-accent">
            <Icon className="size-5" aria-hidden="true" />
          </div>
          <DialogTitle>{current.title}</DialogTitle>
          <DialogDescription asChild>
            <div>{current.body}</div>
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 flex items-center justify-center gap-1.5">
          {steps.map((_, i) => (
            <span
              key={i}
              aria-hidden="true"
              className={
                i === step
                  ? 'size-1.5 rounded-full bg-accent'
                  : 'size-1.5 rounded-full bg-border'
              }
            />
          ))}
        </div>

        <DialogFooter className="flex flex-wrap justify-between gap-2 sm:justify-between">
          <Button variant="ghost" onClick={dismiss}>
            Pomiń
          </Button>
          {isLast ? (
            <Button asChild onClick={dismiss}>
              <Link to={'/media' as never}>
                Zacznij od zdjęć
                <ExternalLink className="size-4" aria-hidden="true" />
              </Link>
            </Button>
          ) : (
            <Button onClick={() => setStep((s) => s + 1)}>
              Dalej
              <ArrowRight className="size-4" aria-hidden="true" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
